import type { QuestionnaireItem, QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import type { QuestionnaireFormLoadedState } from '@medplum/react-hooks';
import type { PaymentTypeCode } from '@unify/fhir-model/resources/episode-of-care';
import { buildQuestionnaireItemPath } from '../pages/appointments/utils/questionnaireResponseRequired';
import type { QuestionnaireFillValidationResult } from './questionnaireResponseFillValidation';

export const TREATMENT_PLAN_INSURANCE_Z_CODE_ERROR =
  'Z codes cannot be used as the primary diagnosis for insurance clients. Choose a billable diagnosis code or mark the client as self-pay if appropriate.';

export function findQuestionnaireItemByLinkIdSuffix(
  items: QuestionnaireItem[] | undefined,
  suffix: string,
): QuestionnaireItem | undefined {
  for (const item of items ?? []) {
    if (item.linkId?.endsWith(suffix)) return item;
    const nested = findQuestionnaireItemByLinkIdSuffix(item.item, suffix);
    if (nested) return nested;
  }
  return undefined;
}

export function filterResponseItemsByLinkId(
  responseItems: QuestionnaireResponseItem[] | undefined,
  linkId: string | undefined,
): QuestionnaireResponseItem[] {
  if (!linkId) return [];
  return (responseItems ?? []).filter((item) => item.linkId === linkId);
}

export function getNestedResponseItem(
  responseItem: QuestionnaireResponseItem | undefined,
  linkIdSuffix: string,
): QuestionnaireResponseItem | undefined {
  return responseItem?.item?.find((child) => child.linkId?.endsWith(linkIdSuffix));
}

export function getAnswerString(
  responseItem: QuestionnaireResponseItem | undefined,
): string {
  return responseItem?.answer?.[0]?.valueString ?? '';
}

export function isCoreTreatmentPlanTopLevelItem(item: QuestionnaireItem): boolean {
  const linkId = item.linkId ?? '';
  return linkId.endsWith('-plan-info') || linkId.endsWith('-diagnosis');
}

function getParentResponseContainer(
  questionnaireResponse: QuestionnaireResponse,
  parentContext: QuestionnaireResponseItem[],
): { item?: QuestionnaireResponseItem[] } {
  if (parentContext.length === 0) {
    return questionnaireResponse;
  }
  return parentContext[parentContext.length - 1];
}

/**
 * Removes a repeating group instance from the questionnaire response tree.
 * Medplum's useQuestionnaireForm exposes onAddGroup but not onRemoveGroup; this mirrors that path.
 */
export function removeRepeatableGroupInstance(
  formState: QuestionnaireFormLoadedState,
  parentContext: QuestionnaireResponseItem[],
  instance: QuestionnaireResponseItem,
  refresh: { context: QuestionnaireResponseItem[]; item: QuestionnaireItem },
): void {
  const parent = getParentResponseContainer(formState.questionnaireResponse, parentContext);
  if (!parent.item) return;

  parent.item = parent.item.filter((child) =>
    instance.id ? child.id !== instance.id : child.linkId !== instance.linkId,
  );

  const refreshResponseItem = refresh.context.length === 0
    ? formState.questionnaireResponse.item?.find((item) => item.linkId === refresh.item.linkId)
    : getNestedResponseItemInContext(formState.questionnaireResponse, refresh.context, refresh.item.linkId);

  const currentAnswer = refreshResponseItem?.answer ?? [];
  formState.onChangeAnswer(refresh.context, refresh.item, currentAnswer);
}

function getNestedResponseItemInContext(
  questionnaireResponse: QuestionnaireResponse,
  context: QuestionnaireResponseItem[],
  linkId: string,
): QuestionnaireResponseItem | undefined {
  let items = questionnaireResponse.item;
  for (const contextItem of context) {
    const next = items?.find((item) =>
      contextItem.id ? item.id === contextItem.id : item.linkId === contextItem.linkId,
    );
    if (!next) return undefined;
    if (contextItem === context[context.length - 1]) {
      return next.item?.find((child) => child.linkId === linkId);
    }
    items = next.item;
  }
  return items?.find((item) => item.linkId === linkId);
}

/** Separates stored `code - description` values; accepts hyphen, en-dash, or em-dash. */
export function splitDiagnosisCodeAndDescription(combined: string): { code: string; description: string } {
  if (!combined) return { code: '', description: '' };
  const normalized = combined.trimStart();
  const match = normalized.match(/^(\S+)\s+[-–—]\s+(.*)$/s);
  if (!match) {
    return { code: normalized, description: '' };
  }
  return { code: match[1]!, description: match[2]! };
}

function resolveDiagnosisCodePart(code: string): string {
  const parsed = splitDiagnosisCodeAndDescription(code);
  return parsed.description ? parsed.code : code;
}

/** Persists diagnosis as `CODE - description`; never stores description without a code. */
export function buildDiagnosisCodeAndDescription(code: string, description: string): string {
  const codePart = resolveDiagnosisCodePart(code);
  if (!codePart) return '';
  if (description === '') return codePart;
  return `${codePart} - ${description}`;
}

/** True when the ICD-10 code starts with Z (case-insensitive). */
export function isZDiagnosisCode(code: string): boolean {
  const codePart = splitDiagnosisCodeAndDescription(code.trim()).code.trim();
  if (!codePart) return false;
  return codePart[0]!.toUpperCase() === 'Z';
}

export function getTreatmentPlanDiagnosisCodeValidationError(
  paymentTypeCode: PaymentTypeCode | undefined,
  diagnosisCode: string,
): string | undefined {
  if (paymentTypeCode !== 'insurance') return undefined;
  if (!isZDiagnosisCode(diagnosisCode)) return undefined;
  return TREATMENT_PLAN_INSURANCE_Z_CODE_ERROR;
}

function findFirstInvalidInsuranceZDiagnosis(
  responseItems: QuestionnaireResponseItem[] | undefined,
  parentPath: string,
): { itemPath: string; linkId: string } | undefined {
  for (const item of responseItems ?? []) {
    const itemPath = buildQuestionnaireItemPath(item.linkId, parentPath);
    if (item.linkId?.endsWith('-diagnosis-code')) {
      const value = getAnswerString(item);
      if (value && isZDiagnosisCode(value)) {
        return { itemPath, linkId: item.linkId };
      }
    }
    const nested = findFirstInvalidInsuranceZDiagnosis(item.item, itemPath);
    if (nested) return nested;
  }
  return undefined;
}

export function validateTreatmentPlanDiagnosisCodesForPaymentType(
  response: Pick<QuestionnaireResponse, 'item'> | undefined,
  paymentTypeCode: PaymentTypeCode | undefined,
): QuestionnaireFillValidationResult {
  if (paymentTypeCode !== 'insurance') return { success: true };

  const invalid = findFirstInvalidInsuranceZDiagnosis(response?.item, '');
  if (!invalid) return { success: true };

  return {
    success: false,
    message: TREATMENT_PLAN_INSURANCE_Z_CODE_ERROR,
    missingLinkIds: [invalid.linkId],
    missingItemPath: invalid.itemPath,
  };
}
